import type { VoiceContextV1 } from '../api/qwen-realtime';

export type QwenVoiceTraceSource = 'frontend-hook' | 'chiedi-screen';

export type QwenVoiceTraceEventType =
  | 'status_changed'
  | 'client_event_sent'
  | 'server_event_received'
  | 'user_transcript_final'
  | 'user_transcript_ignored'
  | 'context_update_queued'
  | 'active_transaction_interrupted'
  | 'audio_guard_released'
  | 'audio_guard_failed'
  | 'audio_guard_post_release_anomaly'
  | 'stale_provider_error_ignored'
  | 'visible_message_final'
  | 'socket_closed';

export interface QwenVoiceTraceContextSummary {
  route?: string;
  visibleImageCount?: number;
  visibleImageIds?: string[];
  visiblePromptKind?: string;
  visiblePromptSnippet?: string;
}

export interface QwenVoiceTraceEvent {
  timestampMs: number;
  source: QwenVoiceTraceSource;
  type: QwenVoiceTraceEventType;
  turnId?: string;
  responseId?: string;
  textSnippet?: string;
  status?: string;
  contextSummary?: QwenVoiceTraceContextSummary;
  closeCode?: number;
  closeReason?: string;
  details?: Record<string, string | number | boolean | null>;
}

declare global {
  interface Window {
    __autoscuolaQwenVoiceTrace?: QwenVoiceTraceEvent[];
    __autoscuolaExportQwenVoiceTrace?: () => QwenVoiceTraceEvent[];
  }
}

const TRACE_STORAGE_KEY = 'asc_qwen_voice_trace_v1';
const TRACE_QUERY_KEY = 'qwenVoiceTrace';
const MAX_TRACE_EVENTS = 300;
const MAX_TEXT_SNIPPET_CHARS = 240;

let testTraceEnabled: boolean | null = null;

function traceWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

function traceEnabled(): boolean {
  if (testTraceEnabled !== null) return testTraceEnabled;
  const win = traceWindow();
  if (!win) return false;
  if (import.meta.env.DEV || import.meta.env.MODE === 'test') return true;
  try {
    if (win.localStorage.getItem(TRACE_STORAGE_KEY) === '1') return true;
  } catch {
    // Ignore unavailable storage.
  }
  try {
    return new URL(win.location.href).searchParams.get(TRACE_QUERY_KEY) === '1';
  } catch {
    return false;
  }
}

function sanitizeTextSnippet(text: string | undefined): string | undefined {
  const normalized = text?.replace(/\s+/g, ' ').trim();
  if (!normalized) return undefined;
  return normalized.length > MAX_TEXT_SNIPPET_CHARS
    ? `${normalized.slice(0, MAX_TEXT_SNIPPET_CHARS - 1)}...`
    : normalized;
}

function sanitizeDetails(
  details: Record<string, string | number | boolean | null | undefined> | undefined,
): Record<string, string | number | boolean | null> | undefined {
  if (!details) return undefined;
  const entries = Object.entries(details).flatMap(([key, value]) => (
    value === undefined ? [] : [[key, value] as const]
  ));
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function sanitizePromptSnippet(text: string | undefined): string | undefined {
  return sanitizeTextSnippet(text);
}

export function summarizeQwenVoiceContext(
  context: VoiceContextV1 | undefined,
): QwenVoiceTraceContextSummary | undefined {
  if (!context) return undefined;
  const visibleImageIds = context.currentVisibleImages
    ?.map((image) => image.id)
    .filter(Boolean)
    .slice(0, 5);
  return {
    ...(context.route ? { route: context.route } : {}),
    ...(visibleImageIds?.length ? {
      visibleImageCount: context.currentVisibleImages?.length ?? visibleImageIds.length,
      visibleImageIds,
    } : {}),
    ...(context.visiblePrompt ? {
      visiblePromptKind: context.visiblePrompt.kind,
      visiblePromptSnippet: sanitizePromptSnippet(context.visiblePrompt.text),
    } : {}),
  };
}

export function recordQwenVoiceTrace(
  event: Omit<QwenVoiceTraceEvent, 'timestampMs'>,
): void {
  if (!traceEnabled()) return;
  const win = traceWindow();
  if (!win) return;
  const trace = win.__autoscuolaQwenVoiceTrace ?? [];
  const fullEvent: QwenVoiceTraceEvent = {
    ...event,
    timestampMs: Math.round(performance.now()),
    ...(event.textSnippet ? { textSnippet: sanitizeTextSnippet(event.textSnippet) } : {}),
    ...(event.details ? { details: sanitizeDetails(event.details) } : {}),
  };
  trace.push(fullEvent);
  if (trace.length > MAX_TRACE_EVENTS) trace.splice(0, trace.length - MAX_TRACE_EVENTS);
  win.__autoscuolaQwenVoiceTrace = trace;
  win.__autoscuolaExportQwenVoiceTrace = () => [...(win.__autoscuolaQwenVoiceTrace ?? [])];
}

export function clearQwenVoiceTraceForTests(): void {
  const win = traceWindow();
  if (win) {
    win.__autoscuolaQwenVoiceTrace = [];
    win.__autoscuolaExportQwenVoiceTrace = () => [...(win.__autoscuolaQwenVoiceTrace ?? [])];
  }
}

export function setQwenVoiceTraceEnabledForTests(enabled: boolean | null): void {
  testTraceEnabled = enabled;
}

